import React, { useMemo } from 'react';
import moment from 'moment';
import { Polyline, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useSelector } from 'react-redux';

import { trackDataSelector } from '../../../features/datacenter/historySlice';
import { gensetFocusAssetIdSelector, gensetTrackReportIdSelector } from '../../../features/map/mapSlice';
import { selectGensetMergedVehicles } from '../../../features/genset/gensetSlice';

const buildRunningRangesUtc = (periods = []) => {
    return (Array.isArray(periods) ? periods : []).map((period) => {
        const start = period?.startTime ? moment(period.startTime, 'YYYY-MM-DD HH:mm:ss').subtract(5, 'hours').subtract(30, 'minutes') : null;
        const end = period?.endTime
            ? moment(period.endTime, 'YYYY-MM-DD HH:mm:ss').subtract(5, 'hours').subtract(30, 'minutes')
            : moment().subtract(5, 'hours').subtract(30, 'minutes');
        if (!start || !end || !start.isValid() || !end.isValid()) return null;
        return { start: start.valueOf(), end: end.valueOf() };
    }).filter(Boolean);
};

const buildRunningSegments = (trackPoints = [], ranges = []) => {
    if (!ranges.length || !trackPoints.length) return [];
    const segments = [];
    let current = [];
    trackPoints.forEach((point) => {
        const stamp = moment.utc(point.dt_tracker, 'YYYY-MM-DD HH:mm:ss', true).valueOf();
        const inRange = ranges.some((range) => stamp >= range.start && stamp <= range.end);
        if (inRange) {
            current.push([point.lat, point.lng]);
        } else if (current.length) {
            segments.push(current);
            current = [];
        }
    });
    if (current.length) segments.push(current);
    return segments;
};

const buildRunningMarkers = (trackPoints = [], ranges = []) => {
    if (!ranges.length || !trackPoints.length) return [];
    const stampIndexPairs = trackPoints
        .map((point, index) => {
            const stamp = moment.utc(point.dt_tracker, 'YYYY-MM-DD HH:mm:ss', true).valueOf();
            if (!Number.isFinite(stamp)) return null;
            return { stamp, index };
        })
        .filter(Boolean);
    if (!stampIndexPairs.length) return [];
    const stamps = stampIndexPairs.map((pair) => pair.stamp);
    const findClosestIndex = (target) => {
        if (!Number.isFinite(target)) return null;
        let lo = 0;
        let hi = stamps.length - 1;
        while (lo < hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (stamps[mid] < target) lo = mid + 1;
            else hi = mid;
        }
        let best = lo;
        if (lo > 0 && Math.abs(stamps[lo - 1] - target) <= Math.abs(stamps[lo] - target)) {
            best = lo - 1;
        }
        return stampIndexPairs[best]?.index ?? null;
    };

    const markers = [];
    const usedIndices = new Set();
    ranges.forEach((range, idx) => {
        const startIndex = findClosestIndex(range.start);
        const endIndex = findClosestIndex(range.end);
        [startIndex, endIndex].forEach((pointIndex, positionIndex) => {
            if (pointIndex === null || pointIndex === undefined) return;
            if (usedIndices.has(pointIndex)) return;
            const point = trackPoints[pointIndex];
            if (!point) return;
            usedIndices.add(pointIndex);
            markers.push({
                lat: point.lat,
                lng: point.lng,
                key: `genset-running-marker-${idx}-${positionIndex}-${pointIndex}`,
            });
        });
    });
    return markers;
};

const OsmGensetTrackLine = () => {
    const reportId = useSelector(gensetTrackReportIdSelector);
    const focusAssetId = useSelector(gensetFocusAssetIdSelector);
    const trackData = useSelector(trackDataSelector);
    const gensetVehicles = useSelector(selectGensetMergedVehicles);

    const { trackPath, runningSegments, runningMarkers, stops } = useMemo(() => {
        if (!reportId || !trackData?.[reportId]?.track) return { trackPath: [], runningSegments: [], runningMarkers: [] };
        const track = trackData[reportId].track;
        const trackPath = track.map((point) => [point.lat, point.lng]);
        const stops = Array.isArray(trackData?.[reportId]?.stops) ? trackData[reportId].stops : [];

        const focused = gensetVehicles.find((vehicle) => {
            const id = String(vehicle?.imei || vehicle?.IMEI || vehicle?.assetId || '').trim();
            return id && id === focusAssetId;
        });
        const runningRanges = buildRunningRangesUtc(focused?.period?.runningPeriods || []);
        const runningSegments = buildRunningSegments(track, runningRanges);
        const runningMarkers = buildRunningMarkers(track, runningRanges);
        return { trackPath, runningSegments, runningMarkers, stops };
    }, [focusAssetId, gensetVehicles, reportId, trackData]);

    if (!reportId || trackPath.length === 0) return null;

    const startFlag = stops.length === 0 || stops[0]?.startIndex !== 0;
    const endFlag = stops.length === 0 || stops[stops.length - 1]?.endIndex !== trackPath.length - 1;
    const startIcon = L.icon({ iconUrl: '/assets/history/def30/mapmarkergreen.png', iconAnchor: [15, 30] });
    const endIcon = L.icon({ iconUrl: '/assets/history/def30/mapmarker.png', iconAnchor: [15, 30] });
    const stopIcon = L.icon({ iconUrl: '/assets/history/def30/parking.png', iconAnchor: [15, 30] });
    const runningCircleSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="9" cy="9" r="7" fill="none" stroke="#ef4444" stroke-width="2"/><circle cx="9" cy="9" r="4" fill="#ef4444" stroke="#b91c1c" stroke-width="1"/></svg>';
    const runningCircleIcon = L.icon({
        iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(runningCircleSvg)}`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });

    return (
        <>
            {startFlag && <Marker position={trackPath[0]} icon={startIcon} />}
            {endFlag && <Marker position={trackPath[trackPath.length - 1]} icon={endIcon} />}
            {stops.map((stop, index) => {
                const stopPoint = trackData?.[reportId]?.track?.[stop.startIndex];
                if (!stopPoint) return null;
                return (
                    <Marker
                        key={`genset-stop-${index}`}
                        position={[stopPoint.lat, stopPoint.lng]}
                        icon={stopIcon}
                    />
                );
            })}
            {runningMarkers.map((marker) => (
                <Marker
                    key={marker.key}
                    position={[marker.lat, marker.lng]}
                    icon={runningCircleIcon}
                />
            ))}
            <Polyline positions={trackPath} pathOptions={{ color: '#2563eb', weight: 3, opacity: 0.85 }} />
            {runningSegments.map((segment, index) => (
                <Polyline key={`genset-running-${index}`} positions={segment} pathOptions={{ color: '#ef4444', weight: 4, opacity: 0.95 }} />
            ))}
        </>
    );
};

export default OsmGensetTrackLine;
